import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { normalizeSheetName, DEFAULT_SHEET_NAME } from './checklistExcelImport';
import { uploadImageToSupabase, formatImageUploadError, CHECKLIST_ISSUES_BUCKET } from './uploadImage';
import './CheckInScanPage.css';
import './StaffChecklistPage.css';
import logo from './assets/newlogo.png';
import dukkan from './assets/dukkan2.jpg';

function localYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(s) {
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function sortTasks(a, b) {
  const sheetOrder = (a.sheet_order ?? 0) - (b.sheet_order ?? 0);
  if (sheetOrder !== 0) return sheetOrder;
  const sheetName = normalizeSheetName(a.sheet_name).localeCompare(normalizeSheetName(b.sheet_name), 'tr');
  if (sheetName !== 0) return sheetName;
  return a.sort_order - b.sort_order;
}

function mapCompletionRow(row) {
  let result = row.result ?? null;
  if (!result && row.done) result = 'ok';
  return {
    result,
    issue_description: row.issue_description ?? '',
    issue_photo_url: row.issue_photo_url ?? '',
  };
}

export default function StaffChecklistPage() {
  const [userId, setUserId] = useState(null);
  const [assignedShopIds, setAssignedShopIds] = useState([]);
  const [shops, setShops] = useState([]);
  const [shopId, setShopId] = useState(null);
  const [ownerUserId, setOwnerUserId] = useState(null);
  const workDate = localYmd(new Date());
  const [tasks, setTasks] = useState([]);
  const [completionByTaskId, setCompletionByTaskId] = useState({});
  const [notes, setNotes] = useState('');
  const [notesStatus, setNotesStatus] = useState('idle');
  const [loading, setLoading] = useState(true);
  const [shopsLoading, setShopsLoading] = useState(true);
  const [error, setError] = useState('');
  const [savingTaskId, setSavingTaskId] = useState(null);
  const [phase, setPhase] = useState('intro');
  const [taskIndex, setTaskIndex] = useState(0);
  /** choose | issue-form | answered */
  const [taskView, setTaskView] = useState('choose');
  const [issueDescription, setIssueDescription] = useState('');
  const [issuePhotoFile, setIssuePhotoFile] = useState(null);
  const [issuePhotoPreview, setIssuePhotoPreview] = useState('');
  const [uploadingIssue, setUploadingIssue] = useState(false);
  const notesBaselineRef = useRef('');
  const notesSaveTimerRef = useRef(null);
  const issuePhotoInputRef = useRef(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data?.user?.id ?? null));
  }, []);

  const loadAssignedShops = useCallback(async () => {
    setShopsLoading(true);
    setError('');
    const { data: ids, error: rpcErr } = await supabase.rpc('staff_my_assigned_shop_ids');
    if (rpcErr) {
      setError(rpcErr.message);
      setAssignedShopIds([]);
      setShops([]);
      setShopsLoading(false);
      return;
    }
    const shopIds = Array.isArray(ids) ? ids.filter((id) => Number.isFinite(id)) : [];
    setAssignedShopIds(shopIds);

    if (!shopIds.length) {
      setShops([]);
      setShopId(null);
      setShopsLoading(false);
      return;
    }

    const { data: shopRows, error: shopErr } = await supabase
      .from('shops')
      .select('id, shop')
      .in('id', shopIds)
      .order('id', { ascending: true });

    if (shopErr) {
      setError(shopErr.message);
      setShops([]);
      setShopsLoading(false);
      return;
    }

    const list = Array.isArray(shopRows) ? shopRows : [];
    setShops(list);
    setShopId((prev) => {
      if (prev != null && shopIds.includes(prev)) return prev;
      return list[0]?.id ?? shopIds[0] ?? null;
    });
    setShopsLoading(false);
  }, []);

  useEffect(() => {
    loadAssignedShops();
  }, [loadAssignedShops]);

  const fetchDay = useCallback(async () => {
    if (!userId || shopId == null) {
      setTasks([]);
      setCompletionByTaskId({});
      setNotes('');
      setOwnerUserId(null);
      notesBaselineRef.current = '';
      setNotesStatus('idle');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const { data: taskRows, error: tErr } = await supabase
      .from('owner_checklist_tasks')
      .select('id, text, sort_order, sheet_name, sheet_order, created_at, user_id')
      .eq('shop_id', shopId)
      .order('sheet_order', { ascending: true })
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (tErr) {
      setError(tErr.message);
      setTasks([]);
      setCompletionByTaskId({});
      setNotes('');
      setOwnerUserId(null);
      notesBaselineRef.current = '';
      setNotesStatus('idle');
      setLoading(false);
      return;
    }

    const list = Array.isArray(taskRows) ? taskRows : [];
    setTasks(list);
    setOwnerUserId(list[0]?.user_id ?? null);

    const map = {};
    if (list.length > 0) {
      const ids = list.map((t) => t.id);
      const { data: compRows, error: cErr } = await supabase
        .from('owner_checklist_completions')
        .select('task_id, done, result, issue_description, issue_photo_url')
        .eq('work_date', workDate)
        .in('task_id', ids);

      if (cErr) {
        setError(cErr.message);
        setCompletionByTaskId({});
        setLoading(false);
        return;
      }

      (Array.isArray(compRows) ? compRows : []).forEach((r) => {
        map[r.task_id] = mapCompletionRow(r);
      });
    }
    setCompletionByTaskId(map);

    const { data: noteRow, error: nErr } = await supabase
      .from('owner_checklist_notes')
      .select('notes')
      .eq('shop_id', shopId)
      .eq('work_date', workDate)
      .maybeSingle();

    if (nErr) {
      setError(nErr.message);
      setNotes('');
      notesBaselineRef.current = '';
    } else {
      const text = noteRow?.notes ?? '';
      setNotes(text);
      notesBaselineRef.current = text;
    }
    setNotesStatus('idle');
    setLoading(false);
  }, [userId, shopId, workDate]);

  useEffect(() => {
    fetchDay();
  }, [fetchDay]);

  const resetIssueForm = useCallback(() => {
    setIssueDescription('');
    setIssuePhotoFile(null);
    setIssuePhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return '';
    });
    if (issuePhotoInputRef.current) issuePhotoInputRef.current.value = '';
  }, []);

  useEffect(() => {
    setPhase('intro');
    setTaskIndex(0);
    setTaskView('choose');
    resetIssueForm();
  }, [shopId, resetIssueForm]);

  useEffect(() => () => {
    if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
    if (issuePhotoPreview) URL.revokeObjectURL(issuePhotoPreview);
  }, [issuePhotoPreview]);

  const orderedTasks = useMemo(() => [...tasks].sort(sortTasks), [tasks]);

  const answeredCount = useMemo(
    () => orderedTasks.filter((t) => completionByTaskId[t.id]?.result).length,
    [orderedTasks, completionByTaskId],
  );

  const okCount = useMemo(
    () => orderedTasks.filter((t) => completionByTaskId[t.id]?.result === 'ok').length,
    [orderedTasks, completionByTaskId],
  );

  const issueCount = useMemo(
    () => orderedTasks.filter((t) => completionByTaskId[t.id]?.result === 'issue').length,
    [orderedTasks, completionByTaskId],
  );

  const sheetSections = useMemo(() => {
    const sections = [];
    let current = null;
    orderedTasks.forEach((task) => {
      const name = normalizeSheetName(task.sheet_name);
      if (!current || current.name !== name) {
        current = { name, tasks: [] };
        sections.push(current);
      }
      current.tasks.push(task);
    });
    return sections;
  }, [orderedTasks]);

  const currentTask = orderedTasks[taskIndex] ?? null;
  const currentCompletion = currentTask ? completionByTaskId[currentTask.id] : null;
  const currentSheetName = currentTask ? normalizeSheetName(currentTask.sheet_name) : DEFAULT_SHEET_NAME;
  const prevSheetName = taskIndex > 0 ? normalizeSheetName(orderedTasks[taskIndex - 1]?.sheet_name) : null;
  const isNewSection = taskIndex > 0 && prevSheetName !== currentSheetName;

  const taskProgressPct = orderedTasks.length
    ? Math.round((answeredCount / orderedTasks.length) * 100)
    : 0;

  const dateLabel = useMemo(() => {
    const dt = parseYmd(workDate);
    if (!dt) return workDate;
    return dt.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' });
  }, [workDate]);

  const shopLabel = shops.find((s) => s.id === shopId)?.shop || '';
  const noShops = !shopsLoading && assignedShopIds.length === 0;

  const firstUnansweredIndex = useMemo(() => {
    const idx = orderedTasks.findIndex((t) => !completionByTaskId[t.id]?.result);
    return idx === -1 ? 0 : idx;
  }, [orderedTasks, completionByTaskId]);

  const syncTaskViewForCurrent = useCallback(() => {
    if (!currentTask) return;
    const comp = completionByTaskId[currentTask.id];
    if (comp?.result) {
      setTaskView('answered');
    } else {
      setTaskView('choose');
      resetIssueForm();
    }
  }, [currentTask, completionByTaskId, resetIssueForm]);

  useEffect(() => {
    if (phase === 'task') syncTaskViewForCurrent();
  }, [phase, taskIndex, syncTaskViewForCurrent]);

  const saveNotes = useCallback(async (text) => {
    if (!userId || shopId == null) return false;
    const trimmed = text ?? '';
    if (trimmed === notesBaselineRef.current) return true;

    let noteOwnerId = ownerUserId;
    if (!noteOwnerId) {
      const { data: resolved, error: resolveErr } = await supabase.rpc(
        'resolve_checklist_owner_for_shop',
        { p_shop_id: shopId },
      );
      if (resolveErr) {
        setError(resolveErr.message);
        setNotesStatus('idle');
        return false;
      }
      noteOwnerId = resolved;
    }
    if (!noteOwnerId) {
      setError('Checklist şablonu bulunamadı; işletme sahibi önce görevleri yüklemelidir.');
      setNotesStatus('idle');
      return false;
    }

    setNotesStatus('saving');
    setError('');
    const { error: upErr } = await supabase
      .from('owner_checklist_notes')
      .upsert(
        { shop_id: shopId, work_date: workDate, user_id: noteOwnerId, notes: trimmed },
        { onConflict: 'shop_id,work_date' },
      );
    if (upErr) {
      setError(upErr.message);
      setNotesStatus('idle');
      return false;
    }
    notesBaselineRef.current = trimmed;
    setNotesStatus('saved');
    return true;
  }, [userId, shopId, workDate, ownerUserId]);

  const scheduleNotesSave = useCallback((text) => {
    if (notesSaveTimerRef.current) clearTimeout(notesSaveTimerRef.current);
    notesSaveTimerRef.current = setTimeout(() => {
      void saveNotes(text);
    }, 700);
  }, [saveNotes]);

  const handleNotesChange = (value) => {
    setNotes(value);
    setNotesStatus('idle');
    scheduleNotesSave(value);
  };

  const upsertCompletion = async (taskId, payload) => {
    setSavingTaskId(taskId);
    setError('');
    const { data, error: upErr } = await supabase
      .from('owner_checklist_completions')
      .upsert(
        { task_id: taskId, work_date: workDate, ...payload },
        { onConflict: 'task_id,work_date' },
      )
      .select('task_id, done, result, issue_description, issue_photo_url')
      .single();
    setSavingTaskId(null);
    if (upErr) {
      setError(upErr.message);
      return false;
    }
    if (data) {
      setCompletionByTaskId((prev) => ({
        ...prev,
        [data.task_id]: mapCompletionRow(data),
      }));
    }
    return true;
  };

  const advanceTask = () => {
    resetIssueForm();
    if (taskIndex < orderedTasks.length - 1) {
      setTaskIndex((i) => i + 1);
    } else {
      setPhase('notes');
    }
  };

  const startChecklist = (resume = false) => {
    setTaskIndex(resume ? firstUnansweredIndex : 0);
    setTaskView('choose');
    resetIssueForm();
    setPhase('task');
  };

  const handleTaskOk = async () => {
    if (!currentTask || !userId) return;
    const ok = await upsertCompletion(currentTask.id, {
      done: true,
      result: 'ok',
      issue_description: null,
      issue_photo_url: null,
      reported_by: userId,
    });
    if (!ok) return;
    advanceTask();
  };

  const handleIssuePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (issuePhotoPreview) URL.revokeObjectURL(issuePhotoPreview);
    setIssuePhotoFile(file);
    setIssuePhotoPreview(URL.createObjectURL(file));
  };

  const handleSubmitIssue = async () => {
    if (!currentTask || !userId) return;
    const desc = issueDescription.trim();
    if (!desc) {
      setError('Sorun açıklaması zorunludur.');
      return;
    }
    if (!issuePhotoFile) {
      setError('Sorun için fotoğraf yükleyin.');
      return;
    }

    setUploadingIssue(true);
    setError('');
    try {
      const photoUrl = await uploadImageToSupabase(issuePhotoFile, {
        bucket: CHECKLIST_ISSUES_BUCKET,
        folder: `issues/${workDate}`,
      });
      const ok = await upsertCompletion(currentTask.id, {
        done: false,
        result: 'issue',
        issue_description: desc,
        issue_photo_url: photoUrl,
        reported_by: userId,
      });
      if (!ok) return;
      advanceTask();
    } catch (err) {
      setError(formatImageUploadError(err, CHECKLIST_ISSUES_BUCKET));
    } finally {
      setUploadingIssue(false);
    }
  };

  const handleChangeAnswer = () => {
    resetIssueForm();
    setTaskView('choose');
  };

  const handlePrevTask = () => {
    resetIssueForm();
    if (taskIndex > 0) {
      setTaskIndex((i) => i - 1);
    } else {
      setPhase('intro');
    }
  };

  const finishChecklist = async () => {
    if (notesSaveTimerRef.current) {
      clearTimeout(notesSaveTimerRef.current);
      notesSaveTimerRef.current = null;
    }
    const ok = await saveNotes(notes);
    if (ok) setPhase('done');
  };

  const allTasksAnswered = orderedTasks.length > 0 && answeredCount === orderedTasks.length;
  const busy = savingTaskId === currentTask?.id || uploadingIssue;

  return (
    <div className="checkin-page staff-checklist-page staff-checklist-wizard">
      <div className="checkin-content">
        <div className="checkin-left staff-checklist-wizard-main">
          <header className="staff-wizard-header">
            <img src={logo} alt="" className="checkin-logo" style={{ height: 56 }} />
            <Link to="/barista" className="staff-wizard-back">← Ana sayfa</Link>
          </header>

          {error ? <p className="checkin-scanned-warn" role="alert">{error}</p> : null}

          {noShops ? (
            <div className="staff-wizard-card">
              <h1 className="staff-wizard-title">Günlük checklist</h1>
              <p className="staff-wizard-sub">
                Size atanmış şube yok. Vardiya müdürünüz veya işletme sahibi Ekip → Şube ataması bölümünden sizi bir mağazaya atamalıdır.
              </p>
            </div>
          ) : loading ? (
            <div className="staff-wizard-card staff-wizard-card--center">
              <p className="staff-wizard-loading">Yükleniyor…</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="staff-wizard-card">
              <h1 className="staff-wizard-title">Günlük checklist</h1>
              <p className="staff-wizard-sub">
                {shopLabel ? `${shopLabel} için ` : ''}
                henüz checklist yüklenmemiş. İşletme sahibi admin panelinden Excel ile görevleri yüklemelidir.
              </p>
            </div>
          ) : (
            <>
              <div className="staff-wizard-meta">
                {shops.length > 1 ? (
                  <select
                    className="staff-wizard-shop-select shift-input"
                    value={shopId ?? ''}
                    onChange={(e) => setShopId(e.target.value ? Number(e.target.value) : null)}
                    disabled={phase !== 'intro' || shopsLoading}
                    aria-label="Mağaza"
                  >
                    {shops.map((s) => (
                      <option key={s.id} value={s.id}>{s.shop || `Mağaza #${s.id}`}</option>
                    ))}
                  </select>
                ) : (
                  <span className="staff-wizard-shop">{shopLabel}</span>
                )}
                <span className="staff-wizard-date">{dateLabel}</span>
              </div>

              {phase !== 'intro' && phase !== 'done' ? (
                <div className="staff-wizard-progress-wrap" aria-hidden>
                  <div
                    className="staff-wizard-progress-bar"
                    style={{
                      width: phase === 'notes'
                        ? '100%'
                        : `${Math.round(((taskIndex + 0.5) / orderedTasks.length) * 100)}%`,
                    }}
                  />
                </div>
              ) : null}

              {phase === 'intro' && (
                <div className="staff-wizard-card staff-wizard-card--intro">
                  <h1 className="staff-wizard-title">Günlük checklist</h1>
                  <p className="staff-wizard-sub">
                    Her görevi tek tek kontrol edin. Tamamsa onaylayın; sorun varsa fotoğraf ve açıklama ekleyin.
                  </p>

                  <div className="staff-wizard-summary">
                    <div className="staff-wizard-stat">
                      <span className="staff-wizard-stat-num">{orderedTasks.length}</span>
                      <span className="staff-wizard-stat-label">görev</span>
                    </div>
                    <div className="staff-wizard-stat staff-wizard-stat--done">
                      <span className="staff-wizard-stat-num">{okCount}</span>
                      <span className="staff-wizard-stat-label">tamam</span>
                    </div>
                    <div className="staff-wizard-stat staff-wizard-stat--issue">
                      <span className="staff-wizard-stat-num">{issueCount}</span>
                      <span className="staff-wizard-stat-label">sorun</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="staff-wizard-play"
                    onClick={() => {
                      if (allTasksAnswered) setPhase('notes');
                      else startChecklist(answeredCount > 0 && answeredCount < orderedTasks.length);
                    }}
                    aria-label={answeredCount > 0 && !allTasksAnswered ? 'Kaldığın yerden devam et' : 'Checklist başlat'}
                  >
                    <span className="staff-wizard-play-icon" aria-hidden>▶</span>
                  </button>
                  <p className="staff-wizard-play-label">
                    {allTasksAnswered
                      ? 'Tüm görevler yanıtlandı — gün notlarına geçebilirsiniz'
                      : answeredCount > 0
                        ? 'Devam etmek için oynat'
                        : 'Başlamak için oynat'}
                  </p>

                  {answeredCount > 0 ? (
                    <div className="staff-wizard-actions staff-wizard-actions--intro-extra">
                      {allTasksAnswered ? (
                        <button type="button" className="staff-wizard-btn staff-wizard-btn--primary" onClick={() => setPhase('notes')}>
                          Gün notlarına geç
                        </button>
                      ) : (
                        <button type="button" className="staff-wizard-btn staff-wizard-btn--ghost" onClick={() => startChecklist(false)}>
                          Baştan başla
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {phase === 'task' && currentTask ? (
                <div className="staff-wizard-card staff-wizard-card--task">
                  <p className="staff-wizard-step-label">
                    Görev {taskIndex + 1} / {orderedTasks.length}
                  </p>

                  {isNewSection || (taskIndex === 0 && sheetSections.length > 1) ? (
                    <p className="staff-wizard-section-badge">{currentSheetName}</p>
                  ) : null}

                  <h2 className="staff-wizard-task-text">{currentTask.text}</h2>

                  {taskView === 'choose' && (
                    <div className="staff-wizard-actions staff-wizard-actions--task">
                      <button
                        type="button"
                        className="staff-wizard-btn staff-wizard-btn--ok staff-wizard-btn--large"
                        onClick={() => void handleTaskOk()}
                        disabled={busy}
                      >
                        {busy ? 'Kaydediliyor…' : '✓ Tamam'}
                      </button>
                      <button
                        type="button"
                        className="staff-wizard-btn staff-wizard-btn--issue staff-wizard-btn--large"
                        onClick={() => {
                          resetIssueForm();
                          setTaskView('issue-form');
                        }}
                        disabled={busy}
                      >
                        ✗ Sorun var
                      </button>
                    </div>
                  )}

                  {taskView === 'issue-form' && (
                    <div className="staff-wizard-issue-form">
                      <p className="staff-wizard-issue-hint">Sorunu fotoğraflayın ve kısaca açıklayın.</p>

                      <label className="staff-wizard-photo-btn">
                        <input
                          ref={issuePhotoInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleIssuePhotoChange}
                          className="staff-wizard-photo-input"
                        />
                        {issuePhotoPreview ? (
                          <img src={issuePhotoPreview} alt="Sorun önizlemesi" className="staff-wizard-photo-preview" />
                        ) : (
                          <span className="staff-wizard-photo-placeholder">
                            <span aria-hidden>📷</span>
                            Fotoğraf çek veya seç
                          </span>
                        )}
                      </label>

                      <textarea
                        className="staff-wizard-notes"
                        value={issueDescription}
                        onChange={(e) => setIssueDescription(e.target.value)}
                        placeholder="Sorunu açıklayın… (zorunlu)"
                        aria-label="Sorun açıklaması"
                        rows={4}
                      />

                      <div className="staff-wizard-actions">
                        <button
                          type="button"
                          className="staff-wizard-btn staff-wizard-btn--issue staff-wizard-btn--large"
                          onClick={() => void handleSubmitIssue()}
                          disabled={busy}
                        >
                          {uploadingIssue ? 'Yükleniyor…' : 'Sorunu kaydet ve devam et'}
                        </button>
                        <button
                          type="button"
                          className="staff-wizard-btn staff-wizard-btn--ghost"
                          onClick={() => setTaskView('choose')}
                          disabled={busy}
                        >
                          ← Geri
                        </button>
                      </div>
                    </div>
                  )}

                  {taskView === 'answered' && currentCompletion?.result && (
                    <div className="staff-wizard-answered">
                      {currentCompletion.result === 'ok' ? (
                        <p className="staff-wizard-answered-ok">✓ Tamam olarak işaretlendi</p>
                      ) : (
                        <div className="staff-wizard-answered-issue">
                          <p className="staff-wizard-answered-issue-title">✗ Sorun bildirildi</p>
                          {currentCompletion.issue_photo_url ? (
                            <a href={currentCompletion.issue_photo_url} target="_blank" rel="noopener noreferrer">
                              <img
                                src={currentCompletion.issue_photo_url}
                                alt="Sorun fotoğrafı"
                                className="staff-wizard-answered-photo"
                              />
                            </a>
                          ) : null}
                          {currentCompletion.issue_description ? (
                            <p className="staff-wizard-answered-desc">{currentCompletion.issue_description}</p>
                          ) : null}
                        </div>
                      )}
                      <div className="staff-wizard-actions">
                        <button type="button" className="staff-wizard-btn staff-wizard-btn--primary" onClick={advanceTask}>
                          Sonraki görev →
                        </button>
                        <button type="button" className="staff-wizard-btn staff-wizard-btn--ghost" onClick={handleChangeAnswer}>
                          Yanıtı değiştir
                        </button>
                      </div>
                    </div>
                  )}

                  {taskView !== 'answered' ? (
                    <div className="staff-wizard-nav-row">
                      <button type="button" className="staff-wizard-link" onClick={handlePrevTask} disabled={busy}>
                        ← Geri
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {phase === 'notes' && (
                <div className="staff-wizard-card staff-wizard-card--notes">
                  <p className="staff-wizard-step-label">
                    Son adım · {answeredCount}/{orderedTasks.length} görev yanıtlandı
                    {issueCount > 0 ? ` · ${issueCount} sorun` : ''}
                  </p>
                  <h2 className="staff-wizard-title staff-wizard-title--sm">Gün notları</h2>
                  <p className="staff-wizard-sub">İsteğe bağlı genel notlar.</p>
                  <textarea
                    className="staff-wizard-notes"
                    value={notes}
                    onChange={(e) => handleNotesChange(e.target.value)}
                    placeholder="Örn: Genel hatırlatmalar…"
                    aria-label="Gün notları"
                    rows={5}
                  />
                  <p className="staff-wizard-notes-status" aria-live="polite">
                    {notesStatus === 'saving' ? 'Kaydediliyor…' : notesStatus === 'saved' ? 'Kaydedildi' : ''}
                  </p>
                  <div className="staff-wizard-actions">
                    <button type="button" className="staff-wizard-btn staff-wizard-btn--primary staff-wizard-btn--large" onClick={() => void finishChecklist()}>
                      Checklist&apos;i bitir
                    </button>
                    <button
                      type="button"
                      className="staff-wizard-btn staff-wizard-btn--ghost"
                      onClick={() => {
                        setTaskIndex(Math.max(orderedTasks.length - 1, 0));
                        setPhase('task');
                      }}
                    >
                      ← Görevlere dön
                    </button>
                  </div>
                </div>
              )}

              {phase === 'done' && (
                <div className="staff-wizard-card staff-wizard-card--done">
                  <div className="staff-wizard-done-icon" aria-hidden>✓</div>
                  <h2 className="staff-wizard-title">Bugünkü checklist tamam!</h2>
                  <p className="staff-wizard-sub">
                    {okCount} tamam · {issueCount} sorun bildirimi
                    {notes.trim() ? ' · Gün notu kaydedildi' : ''}.
                  </p>
                  <div className="staff-wizard-ring" style={{ '--pct': `${taskProgressPct}%` }}>
                    <span>{taskProgressPct}%</span>
                  </div>
                  <div className="staff-wizard-actions">
                    <Link to="/barista" className="staff-wizard-btn staff-wizard-btn--primary">Ana sayfaya dön</Link>
                    <button type="button" className="staff-wizard-btn staff-wizard-btn--ghost" onClick={() => setPhase('intro')}>
                      Checklist&apos;e tekrar bak
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="checkin-right" aria-hidden>
          <img src={dukkan} alt="" />
        </div>
      </div>
    </div>
  );
}
